import { useState, useEffect, useCallback } from 'react';
import {
  Users,
  MessageCircle,
  AlertTriangle,
  Shield,
  Activity,
  Clock,
  TrendingUp,
  LogOut,
  Lock,
} from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'toonah_admin_token';

const AdminLogin = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data?.token) {
        sessionStorage.setItem(TOKEN_KEY, res.data.token);
        onSuccess(res.data.token);
      } else {
        setError('Unexpected response');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid password');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-slate-800/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-blue-600/20 border border-blue-500/40 flex items-center justify-center">
            <Lock className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Admin Access</h1>
            <p className="text-xs text-slate-400">Toonah Dashboard</p>
          </div>
        </div>

        <label className="block text-sm text-slate-300 mb-2" htmlFor="admin-password">
          Admin Password
        </label>
        <input
          id="admin-password"
          data-testid="admin-password-input"
          type="password"
          autoFocus
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-slate-900/70 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition"
          placeholder="Enter admin password"
        />

        {error && (
          <div
            data-testid="admin-login-error"
            className="mt-3 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !password}
          data-testid="admin-login-submit"
          className="mt-5 w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition"
        >
          {submitting ? 'Signing in…' : 'Sign In'}
        </button>
      </form>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    const current = sessionStorage.getItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
    setDashboard(null);
    if (current) {
      try {
        await axios.post(
          `${API}/admin/logout`,
          {},
          { headers: { Authorization: `Bearer ${current}` } }
        );
      } catch {
        /* ignore */
      }
    }
  }, []);

  const fetchDashboard = useCallback(async (bearer) => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${bearer}` },
      });
      setDashboard(res.data);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
        // Session expired / revoked — force re-login
        sessionStorage.removeItem(TOKEN_KEY);
        setToken('');
        setDashboard(null);
      }
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    fetchDashboard(token);
    const interval = setInterval(() => fetchDashboard(token), 10000);
    return () => clearInterval(interval);
  }, [token, fetchDashboard]);

  if (!token) {
    return (
      <>
        <SEO title="Admin — Toonah" description="Toonah admin dashboard." path="/admin" robots="noindex,nofollow" />
        <AdminLogin onSuccess={(t) => setToken(t)} />
      </>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <SEO title="Admin — Toonah" description="Toonah admin dashboard." path="/admin" robots="noindex,nofollow" />
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
      <SEO title="Admin — Toonah" description="Toonah admin dashboard." path="/admin" robots="noindex,nofollow" />
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-black text-white mb-2">Admin Dashboard</h1>
            <p className="text-slate-400">Toonah Platform Management</p>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 text-sm font-semibold transition"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="glass-card p-6 rounded-2xl">
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8 text-blue-500" />
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-3xl font-black text-white mb-1">
              {dashboard?.stats?.users_online || 0}
            </div>
            <div className="text-sm text-slate-400">Users Online</div>
          </div>

          <div className="glass-card p-6 rounded-2xl">
            <div className="flex items-center justify-between mb-4">
              <MessageCircle className="w-8 h-8 text-cyan-500" />
              <Activity className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-3xl font-black text-white mb-1">
              {dashboard?.stats?.active_chats || 0}
            </div>
            <div className="text-sm text-slate-400">Active Chats</div>
          </div>

          <div className="glass-card p-6 rounded-2xl">
            <div className="flex items-center justify-between mb-4">
              <AlertTriangle className="w-8 h-8 text-yellow-500" />
              <Clock className="w-5 h-5 text-yellow-500" />
            </div>
            <div className="text-3xl font-black text-white mb-1">
              {dashboard?.stats?.total_reports || 0}
            </div>
            <div className="text-sm text-slate-400">Total Reports</div>
          </div>

          <div className="glass-card p-6 rounded-2xl">
            <div className="flex items-center justify-between mb-4">
              <Shield className="w-8 h-8 text-green-500" />
              <Activity className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-3xl font-black text-white mb-1">
              {dashboard?.active_rooms || 0}
            </div>
            <div className="text-sm text-slate-400">Active Rooms</div>
          </div>
        </div>

        {/* Recent Reports */}
        <div className="glass-card p-6 rounded-2xl mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">Recent Reports</h2>
          <div className="space-y-3">
            {dashboard?.recent_reports?.length > 0 ? (
              dashboard.recent_reports.map((report, i) => (
                <div key={i} className="bg-slate-800/50 p-4 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-white">{report.reason}</span>
                    <span className="text-xs text-slate-400">
                      {new Date(report.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm text-slate-400">
                    Reported by: {report.reported_by_session.slice(0, 8)}...
                  </div>
                  {report.description && (
                    <div className="text-sm text-slate-500 mt-1">{report.description}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-slate-400 text-center py-8">No reports yet</div>
            )}
          </div>
        </div>

        {/* Recent Moderation */}
        <div className="glass-card p-6 rounded-2xl">
          <h2 className="text-2xl font-bold text-white mb-4">Moderation Events</h2>
          <div className="space-y-3">
            {dashboard?.recent_moderation?.length > 0 ? (
              dashboard.recent_moderation.map((log, i) => (
                <div key={i} className="bg-slate-800/50 p-4 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-yellow-400">{log.violation_type}</span>
                    <span className="text-xs text-slate-400">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">
                      Session: {log.session_id.slice(0, 8)}...
                    </span>
                    <span className="text-sm text-white">Action: {log.action_taken}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-slate-400 text-center py-8">No moderation events yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
